import {
  QueuedToast,
  ToastState as StatelyToastState,
} from "@react-stately/toast";
import { ReactNode, useEffect, useRef } from "react";
import { tv } from "tailwind-variants";
import { DenovoIcon } from "../icons";
import { IconName } from "../icons/interfaces";
import { ToastPosition, ToastVariant } from "./enums";

const toastStyles = tv({
  base: "fixed flex items-center gap-3 p-3 z-60 shadow-lg rounded-lg border border-borderTertiary border-l-4 bg-backgroundTertiary transition-colors duration-base ease-out",
  variants: {
    position: {
      [ToastPosition.Top]: "top-4 left-1/2 -translate-x-1/2 w-max max-w-md",
      [ToastPosition.Bottom]:
        "bottom-4 left-1/2 -translate-x-1/2 max-w-3xl w-full",
      [ToastPosition.TopLeft]: "top-4 left-4 max-w-xs",
      [ToastPosition.TopRight]: "top-4 right-4 max-w-xs",
      [ToastPosition.BottomRight]: "bottom-4 right-4 max-w-xs",
      [ToastPosition.BottomLeft]: "bottom-4 left-4 max-w-xs",
      [ToastPosition.Left]: "top-1/2 left-4 -translate-y-1/2 max-w-xs",
      [ToastPosition.Right]: "top-1/2 right-4 -translate-y-1/2 max-w-xs",
    },
    variant: {
      [ToastVariant.Success]: "border-l-borderPositive",
      [ToastVariant.Warning]: "border-l-borderWarning",
      [ToastVariant.Danger]: "border-l-borderAlert",
    },
    animation: {
      entering: "animate-slide-down",
      exiting: "animate-slide-up",
      idle: "",
    },
  },
  defaultVariants: {
    variant: ToastVariant.Success,
    position: ToastPosition.Top,
    animation: "entering",
  },
});

const iconWrapperStyles = tv({
  base: "flex items-center justify-center shrink-0 h-7 w-7",
  variants: {
    variant: {
      [ToastVariant.Success]: "text-contentPositive",
      [ToastVariant.Warning]: "text-contentWarning",
      [ToastVariant.Danger]: "text-contentAlert",
    },
  },
  defaultVariants: {
    variant: ToastVariant.Success,
  },
});

const variantIcons: Record<ToastVariant, IconName> = {
  [ToastVariant.Success]: "rounded-check",
  [ToastVariant.Warning]: "alert",
  [ToastVariant.Danger]: "fire",
};

export interface IToastState {
  children: ReactNode;
  variant: ToastVariant;
  position?: ToastPosition;
}

export interface IToastProps {
  toast: QueuedToast<IToastState> & { animation?: any };
  state: StatelyToastState<IToastState> & { remove?: (key: string) => void };
}
export function Toast({ toast, state }: IToastProps) {
  const {
    key,
    animation,
    timer,
    content: { children, variant, position },
  } = toast;

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // @ts-ignore
    if (timer?.remaining === 0) {
      state.close(key);
      state.remove?.call(undefined, key);
    }
    state.resumeAll();
    // @ts-ignore
  }, [timer?.remaining, key, state]);

  return (
    <div
      ref={ref}
      className={toastStyles({ variant, position })}
      data-animation={animation}
      onAnimationEnd={() => {
        if (animation === "exiting") {
          state.remove?.call(undefined, key);
          state.close(key);
        }
      }}>
      <div className={iconWrapperStyles({ variant })}>
        <DenovoIcon icon={variantIcons[variant]} className="w-7 h-7" />
      </div>
      <div className="flex-1 text-pl text-contentSecondary">{children}</div>
    </div>
  );
}
